/**
 * Voucher API Requests
 *
 * Hooks for voucher-related API calls via the Marketing Service (Federation Gateway).
 *
 * @module @kidslabs/module-flash-sale/api/requests/voucher
 */

import {
    DisplayLocation,
    useMarketingGetAvailableVouchersQuery,
    useMarketingGetAvailableVouchersLazyQuery,
    useMarketingGetAppliedCodesQuery,
    useMarketingGetAppliedCodesLazyQuery,
    type MarketingGetAvailableVouchersQueryVariables,
} from '../../types/marketing.graphql';

/**
 * Hook for fetching available voucher allocations from Marketing Service.
 *
 * Retrieves voucher allocations eligible for the current user/cart,
 * filtered by display location.
 */
export const useAvailableVouchers = (variables?: MarketingGetAvailableVouchersQueryVariables) => {
    const { data, loading, error, refetch } = useMarketingGetAvailableVouchersQuery({
        fetchPolicy: 'network-only',
        variables: {
            location: DisplayLocation.CART,
            ...variables,
        },
    });

    return {
        availableVouchers: data?.marketingGetAvailableVouchers ?? [],
        loading,
        error,
        refetch,
    };
};

/**
 * Lazy version of useAvailableVouchers for on-demand fetching.
 */
export const useAvailableVouchersLazy = () => {
    const [fetchVouchers, { data, loading, error, refetch }] = useMarketingGetAvailableVouchersLazyQuery({
        fetchPolicy: 'network-only',
    });

    return {
        fetchVouchers,
        availableVouchers: data?.marketingGetAvailableVouchers ?? [],
        loading,
        error,
        refetch,
    };
};

/**
 * Hook for fetching applied codes (coupons + vouchers) from Marketing Service.
 *
 * Returns both applied coupons and applied vouchers for the given cart.
 */
export const useMarketingAppliedCodes = ({ cartId }: { cartId: string }) => {
    const { data, loading, error, refetch } = useMarketingGetAppliedCodesQuery({
        fetchPolicy: 'network-only',
        variables: { cart_id: cartId },
        skip: !cartId,
    });

    return {
        appliedCoupons: data?.marketingGetAppliedCodes?.coupons ?? [],
        appliedVouchers: data?.marketingGetAppliedCodes?.vouchers ?? [],
        loading,
        error,
        refetch,
    };
};

/**
 * Lazy version of useMarketingAppliedCodes for on-demand fetching.
 */
export const useMarketingAppliedCodesLazy = ({ cartId }: { cartId: string }) => {
    const [fetchAppliedCodes, { data, loading, error, refetch }] = useMarketingGetAppliedCodesLazyQuery({
        fetchPolicy: 'network-only',
        variables: { cart_id: cartId },
    });

    return {
        fetchAppliedCodes,
        appliedCoupons: data?.marketingGetAppliedCodes?.coupons ?? [],
        appliedVouchers: data?.marketingGetAppliedCodes?.vouchers ?? [],
        loading,
        error,
        refetch,
    };
};
